import { AgreementTermType } from '../entities'

export const contractTypeSupportsRenewal: Record<AgreementTermType, boolean> = {
  fixed: true,
  perpetual: true,
  rolling: true,
  oneOff: false, // Explicit product decision since it wouldn't make sense
}

export const shouldCreateExtraTermForContractType: Record<AgreementTermType, boolean> = {
  fixed: true,
  perpetual: false,
  rolling: false,
  oneOff: false,
}

export const contractTypeRequiresEndDate: Record<AgreementTermType, boolean> = {
  fixed: true,
  perpetual: false,
  rolling: false,
  oneOff: false,
}

export const pricingRowRequiresEndDate: Record<AgreementTermType, boolean> = {
  // Note(Diego): fixed and one-off contracts are finite and require an end date
  fixed: true,
  oneOff: true,
  // Note(Diego): perpetual and rolling contracts are ongoing and do not require an end date. Could be indefinite duration
  perpetual: false,
  rolling: false,
}

export type EndDateEvent = 'renews' | 'ends'

// What happens on a term's end date by the contract's own terms. Only fixed terms record whether they
// auto-renew; rolling and perpetual terms carry on by nature.
const endDateEventByTermType: Record<AgreementTermType, EndDateEvent | 'renewsIfAutoRenew'> = {
  fixed: 'renewsIfAutoRenew',
  perpetual: 'renews',
  rolling: 'renews',
  oneOff: 'ends',
}

export const getTermTypeEndDateEvent = ({ type, autoRenew }: { type: AgreementTermType; autoRenew: boolean }): EndDateEvent => {
  const event = endDateEventByTermType[type]
  if (event === 'renewsIfAutoRenew') return autoRenew ? 'renews' : 'ends'
  return event
}

const termTypesWhere = <T>(rule: Record<AgreementTermType, T>, applies: T) =>
  Object.entries(rule)
    .filter(([, value]) => value === applies)
    .map(([termType]) => termType)

export const renewableTermTypes = termTypesWhere(contractTypeSupportsRenewal, true)
export const nonRenewableTermTypes = termTypesWhere(contractTypeSupportsRenewal, false)
export const termTypesRequiringEndDate = termTypesWhere(contractTypeRequiresEndDate, true)
export const termTypesRenewingAtEndDate = termTypesWhere(endDateEventByTermType, 'renews')
export const termTypesRenewingIfAutoRenew = termTypesWhere(endDateEventByTermType, 'renewsIfAutoRenew')
